import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format, addDays, formatDistanceStrict, isToday } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

const LONG_DURATION_THRESHOLD_DAYS = 90;

const localeFor = (lang) => {
  if (lang?.startsWith('fr')) return fr;
  // Malagasy isn't shipped by date-fns — fall back to en-US for date math.
  return enUS;
};

/**
 * Inline preview shown under any "duration in days" admin input.
 *
 * Computes (today + days) and displays "Listings created today will expire on
 * <DATE> (in N days)" with full i18n + locale-aware formatting. Hides itself
 * when the input is empty or invalid (returns null).
 */
export const ExpirationPreview = ({ days, testId = 'expiration-preview' }) => {
  const { t, i18n } = useTranslation();

  const result = useMemo(() => {
    if (days === null || days === undefined || days === '') return null;
    const n = Number(days);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return null;

    const locale = localeFor(i18n.language);
    const target = addDays(new Date(), n);
    const dateStr = format(target, 'd MMMM yyyy', { locale });
    const relative = n === 0
      ? null
      : formatDistanceStrict(target, new Date(), { addSuffix: true, locale });

    let label;
    if (n === 0 || isToday(target)) {
      label = t('admin.lifecycle.expirationToday');
    } else if (relative) {
      label = t('admin.lifecycle.expirationPreviewWithRelative', {
        date: dateStr,
        relative,
      });
    } else {
      label = t('admin.lifecycle.expirationPreview', { date: dateStr });
    }

    return { label, isLong: n > LONG_DURATION_THRESHOLD_DAYS };
  }, [days, i18n.language, t]);

  if (!result) return null;

  return (
    <p
      className={`mt-1.5 text-xs ${
        result.isLong ? 'text-warning' : 'text-text_muted'
      }`}
      data-testid={testId}
    >
      {result.label}
      {result.isLong && (
        <span className="block mt-0.5">
          {t('admin.lifecycle.expirationLongWarning')}
        </span>
      )}
    </p>
  );
};
